import LogUtils from './log_utils';
import {LIBRARY_NAME} from 'src/js/common/constants';

const logger = LogUtils.getLogger('PromiseUtils', LIBRARY_NAME);

/**
 * @type {{makeCancelable: (function(*): {cancel(): void, promise: Promise<any>})}}
 * @see https://reactjs.org/blog/2015/12/16/ismounted-antipattern.html
 */

export interface CancelablePromise<T = void> {
  promise: Promise<T>;
  cancel: () => void;
}

export interface HandleIsCanceled {
  isCanceled: boolean;
}

export interface PromiseUtilsType<T = void> {
  makeCancelable: (promise: Promise<T>) => CancelablePromise<T>;
  handleCanceledPromise: (error: HandleIsCanceled | string) => void;
}

export function isHandleIsCanceled(
  error: HandleIsCanceled | string
): error is HandleIsCanceled {
  return (
    error !== undefined && (error as HandleIsCanceled).isCanceled !== undefined
  );
}

export const PromiseUtils: PromiseUtilsType = {
  /* eslint-disable prefer-promise-reject-errors */
  makeCancelable: <T>(promise: Promise<T>) => {
    let hasCanceled_ = false;

    const wrappedPromise = new Promise<T>((resolve, reject) => {
      promise.then(
        (val: T) => (hasCanceled_ ? reject({isCanceled: true}) : resolve(val)),
        (error) => (hasCanceled_ ? reject({isCanceled: true}) : reject(error))
      );
    });

    return {
      promise: wrappedPromise,
      cancel() {
        hasCanceled_ = true;
      },
    };
  },

  /**
   * Reports warning if promise was cancel, otherwise rethrows the error.
   * @param error
   */
  handleCanceledPromise(error) {
    if (isHandleIsCanceled(error)) {
      if (error.isCanceled) {
        // promise was canceled, report it
        logger.warn('Promise was canceled, probably due the unmount.');
      }
    } else {
      // if other error occurred rethrow it
      throw new Error(error);
    }
  },
  /* eslint-enable prefer-promise-reject-errors */
};
